import { useT } from "../../../../shared/i18n/i18n";
import { Archive, CircleDot, FileUp, LayoutTemplate, MoreHorizontal, Settings2, Shield } from "lucide-react";
import { type ReactElement, type ReactNode, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuGroup,
  ContextMenuItem,
  ContextMenuShortcut,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatMenuShortcut, matchesMenuShortcut, type MenuShortcut } from "../../../../shared/lib/menu-shortcuts";

type BoardAction = {
  disabled?: boolean;
  icon?: ReactNode;
  id: string;
  label: string;
  onSelect?: () => void;
  shortcut?: MenuShortcut;
  variant?: "default" | "destructive";
};

type BoardActionsMenuProps = {
  archiveDescription?: string;
  archiveTitle: string;
  ariaLabel: string;
  children?: (optionsTrigger: ReactNode) => ReactElement;
  disabled?: boolean;
  dropdownTrigger?: ReactElement;
  onArchive: () => Promise<void>;
  onOpenStatuses: () => void;
  // Offered only where applying a starting configuration makes sense.
  onOpenTemplates?: () => void;
  onOpenImport?: () => void;
  onOpenSettings: () => void;
  onOpenPermissions: () => void;
};

export function BoardActionsMenu({
  archiveDescription = "This board and its board content will be archived now and deleted after 30 days.",
  archiveTitle,
  ariaLabel,
  children,
  disabled = false,
  dropdownTrigger,
  onArchive,
  onOpenStatuses,
  onOpenTemplates,
  onOpenImport,
  onOpenSettings,
  onOpenPermissions,
}: BoardActionsMenuProps) {
  const [isArchiveOpen, setIsArchiveOpen] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isContextOpen, setIsContextOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const t = useT();
  const actions: BoardAction[] = [
    {
      disabled,
      icon: <Settings2 data-icon="inline-start" />,
      id: "settings",
      label: t("boardActions.settings"),
      onSelect: openSettings,
      shortcut: { shortcut: "Mod+," },
    },
    {
      disabled,
      icon: <CircleDot data-icon="inline-start" />,
      id: "statuses",
      label: t("boardActions.statuses"),
      onSelect: openStatuses,
    },
    ...(onOpenImport
      ? [
          {
            disabled,
            icon: <FileUp data-icon="inline-start" />,
            id: "import",
            label: t("boardActions.importItems"),
            onSelect: openImport,
          } satisfies BoardAction,
        ]
      : []),
    ...(onOpenTemplates
      ? [
          {
            disabled,
            icon: <LayoutTemplate data-icon="inline-start" />,
            id: "templates",
            label: t("boardActions.applyTemplate"),
            onSelect: openTemplates,
          } satisfies BoardAction,
        ]
      : []),
    {
      icon: <Shield data-icon="inline-start" />,
      id: "permissions",
      label: t("boardActions.accessShare"),
      onSelect: openPermissions,
      shortcut: { shortcut: "Mod+Shift+P" },
    },
    {
      disabled,
      icon: <Archive data-icon="inline-start" />,
      id: "archive",
      label: t("boardActions.archiveBoard"),
      onSelect: openArchiveDialog,
      shortcut: { linux: "Ctrl+Backspace", macos: "Mod+Backspace", shortcut: "Mod+Backspace", windows: "Ctrl+Backspace" },
      variant: "destructive",
    },
  ];

  async function archive() {
    setIsArchiving(true);
    try {
      await onArchive();
      setIsArchiveOpen(false);
      closeMenus();
    } finally {
      setIsArchiving(false);
    }
  }

  function closeMenus() {
    setIsContextOpen(false);
    setIsDropdownOpen(false);
  }

  function openArchiveDialog() {
    closeMenus();
    window.setTimeout(() => setIsArchiveOpen(true), 0);
  }

  function openSettings() {
    closeMenus();
    window.setTimeout(onOpenSettings, 0);
  }

  function openPermissions() {
    closeMenus();
    window.setTimeout(onOpenPermissions, 0);
  }

  function openStatuses() {
    closeMenus();
    window.setTimeout(onOpenStatuses, 0);
  }

  function openTemplates() {
    closeMenus();
    if (onOpenTemplates) window.setTimeout(onOpenTemplates, 0);
  }

  function openImport() {
    closeMenus();
    if (onOpenImport) window.setTimeout(onOpenImport, 0);
  }

  function handleMenuKeyDown(event: React.KeyboardEvent) {
    for (const action of actions) {
      if (action.disabled || !action.onSelect || !matchesMenuShortcut(event, action.shortcut)) {
        continue;
      }
      event.preventDefault();
      event.stopPropagation();
      action.onSelect();
      return;
    }
  }

  const optionsTrigger = (
    <DropdownMenu modal={false} onOpenChange={setIsDropdownOpen} open={isDropdownOpen}>
      <DropdownMenuTrigger
        render={
          dropdownTrigger ?? (
            <button aria-label={ariaLabel} type="button">
              <MoreHorizontal data-icon="inline-start" />
            </button>
          )
        }
      />
      <DropdownMenuContent align="end" className="w-52" onClick={(event) => event.stopPropagation()} onKeyDown={handleMenuKeyDown}>
        <DropdownMenuGroup>{actions.map((action) => renderDropdownAction(action))}</DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <AlertDialog onOpenChange={setIsArchiveOpen} open={isArchiveOpen}>
      {children ? (
        <ContextMenu onOpenChange={setIsContextOpen}>
          <ContextMenuTrigger render={children(optionsTrigger)} />
          <ContextMenuContent className="w-52" onClick={(event) => event.stopPropagation()} onKeyDown={handleMenuKeyDown}>
            <ContextMenuGroup>{actions.map((action) => renderContextAction(action))}</ContextMenuGroup>
          </ContextMenuContent>
        </ContextMenu>
      ) : (
        optionsTrigger
      )}
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{archiveTitle}</AlertDialogTitle>
          <AlertDialogDescription>{archiveDescription}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isArchiving}>{t("common.cancel")}</AlertDialogCancel>
          <AlertDialogAction disabled={isArchiving} onClick={() => void archive()} variant="destructive">
            {isArchiving ? t("board.archiving") : t("board.archive")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function renderDropdownAction(action: BoardAction) {
  const shortcut = formatMenuShortcut(action.shortcut);

  return (
    <DropdownMenuItem
      disabled={action.disabled}
      key={action.id}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        action.onSelect?.();
      }}
      variant={action.variant}
    >
      {action.icon}
      {action.label}
      {shortcut ? <DropdownMenuShortcut>{shortcut}</DropdownMenuShortcut> : null}
    </DropdownMenuItem>
  );
}

function renderContextAction(action: BoardAction) {
  const shortcut = formatMenuShortcut(action.shortcut);

  return (
    <ContextMenuItem
      disabled={action.disabled}
      key={action.id}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        action.onSelect?.();
      }}
      variant={action.variant}
    >
      {action.icon}
      {action.label}
      {shortcut ? <ContextMenuShortcut>{shortcut}</ContextMenuShortcut> : null}
    </ContextMenuItem>
  );
}
